/** Create decorative wildlife and transports outside gameplay ownership.
 * The world owns registered nodes/resources; active simulation time drives poses. */
export function createSkyAmbientWorld(T, models, own, add, reduced, mergeGeometries) {
  // Decorative wildlife never enters the domain arrays or the flight corridor.
  // Three small instance batches provide ten recognizable flapping silhouettes.
  const birdCount = 10;
  const birdMaterial = own(
    new T.MeshStandardMaterial({ color: '#324752', roughness: 0.9, side: T.DoubleSide }),
  );
  const bodyParts = [
    new T.SphereGeometry(1, 8, 6).scale(0.12, 0.1, 0.4),
    new T.ConeGeometry(0.065, 0.22, 4).rotateX(Math.PI / 2).translate(0, 0, 0.47),
    new T.ConeGeometry(0.14, 0.34, 3).rotateX(-Math.PI / 2).translate(0, 0, -0.42),
  ];
  const bodyGeometry = own(mergeGeometries(bodyParts, false));
  for (const part of bodyParts) part.dispose();
  const wingOutline = new T.Shape();
  wingOutline.moveTo(0, 0.2);
  for (const [x, z] of [
    [0.45, 0.25],
    [0.9, 0.08],
    [1.5, -0.5],
    [1.02, -0.27],
    [1.27, -0.6],
    [0.86, -0.38],
    [0.97, -0.64],
    [0.6, -0.46],
    [0.35, -0.5],
    [0, -0.3],
  ])
    wingOutline.lineTo(x, z);
  wingOutline.closePath();
  const wingGeometry = own(new T.ShapeGeometry(wingOutline, 1).rotateX(Math.PI / 2));
  const birdBodies = own(new T.InstancedMesh(bodyGeometry, birdMaterial, birdCount));
  const birdLeft = own(new T.InstancedMesh(wingGeometry, birdMaterial, birdCount));
  const birdRight = own(new T.InstancedMesh(wingGeometry, birdMaterial, birdCount));
  birdBodies.name = 'sky-ambient-bird-bodies';
  birdLeft.name = 'sky-ambient-left-wings';
  birdRight.name = 'sky-ambient-right-wings';
  const birdMeshes = [birdBodies, birdLeft, birdRight];
  for (const mesh of birdMeshes) {
    mesh.frustumCulled = false;
    add(mesh);
  }
  const birdPose = new T.Object3D(),
    wingPose = new T.Object3D(),
    birdMatrix = new T.Matrix4();
  const transports = [
    models.enemy({ kind: 'manta' }),
    models.enemy({ kind: 'boss', profile: 'carrier', form: 'titan' }),
  ];
  transports[0].scale.setScalar(2.1);
  transports[1].scale.setScalar(0.75);
  transports.forEach((transport, i) => {
    transport.name = `sky-ambient-transport-${i}`;
    add(transport);
  });
  function drawAmbient(state, stage, decoration) {
    const quiet = reduced(),
      time = state.time;
    const ambientBirdCount = Math.ceil(birdCount * decoration.birds);
    const birdsVisible =
      !quiet && ambientBirdCount > 0 && !['tunnel', 'void'].includes(stage.biome);
    for (const mesh of birdMeshes) mesh.count = ambientBirdCount;
    for (const mesh of birdMeshes) mesh.visible = birdsVisible;
    if (birdsVisible)
      for (let i = 0; i < ambientBirdCount; i++) {
        const side = i % 2 ? -1 : 1,
          phase = i * 0.83,
          angle = time * 0.22 + phase;
        birdPose.position.set(
          side * (20 + (i % 5) * 3 + Math.sin(angle) * 2),
          12 + (i % 3) * 2 + Math.sin(angle * 1.3) * 1.2,
          -35 - (i % 5) * 16 + Math.cos(angle) * 9,
        );
        birdPose.rotation.set(
          0,
          Math.atan2(side * Math.cos(angle) * 0.44, -Math.sin(angle) * 1.98),
          Math.sin(angle) * 0.12,
        );
        birdPose.scale.setScalar(0.65 + (i % 3) * 0.15);
        birdPose.updateMatrix();
        birdBodies.setMatrixAt(i, birdPose.matrix);
        const flap = Math.sin(time * (5.5 + (i % 3)) + phase) * 0.65;
        for (const [wing, sign] of [
          [birdLeft, -1],
          [birdRight, 1],
        ]) {
          wingPose.position.set(sign * 0.08, 0, 0);
          wingPose.rotation.set(0, 0, sign * flap);
          wingPose.scale.set(sign, 1, 1);
          wingPose.updateMatrix();
          birdMatrix.multiplyMatrices(birdPose.matrix, wingPose.matrix);
          wing.setMatrixAt(i, birdMatrix);
        }
      }
    if (birdsVisible) for (const mesh of birdMeshes) mesh.instanceMatrix.needsUpdate = true;
    for (let i = 0; i < transports.length; i++) {
      const transport = transports[i];
      transport.visible = !quiet && !state.leviathan && i < decoration.transports;
      if (!transport.visible) continue;
      const side = i ? -1 : 1,
        angle = time * 0.09 + i * 2;
      const x = side * (32 + Math.sin(angle) * 12),
        y = 16 + i * 5 + Math.sin(time * 0.19 + i) * 2.5;
      const z = -95 - i * 22 + Math.cos(angle) * 25;
      const vx = side * Math.cos(angle) * 1.08,
        vz = -Math.sin(angle) * 2.25;
      transport.position.set(x, y, z);
      models.animateEnemy(
        transport,
        { kind: 'manta', age: time, x, y, vx, vy: Math.cos(time * 0.19 + i) * 0.475 },
        false,
      );
      transport.rotation.y = Math.atan2(vx, vz);
    }
  }
  return drawAmbient;
}
